import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Button } from '@/components/shadcn/button'
import { Calendar } from '@/components/shadcn/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import { ScrollArea, ScrollBar } from '@/components/shadcn/scroll-area'
import { format } from 'date-fns'
import { cn } from '@/lib/utils'
import { BaseFieldProps } from './types'
import { IconCalendarClockOutline } from '@/components/icons'
import clsx from 'clsx'

export const DateTimeField: React.FC<BaseFieldProps> = ({ field, formFieldName, control }) => {
  const hours = Array.from({ length: 12 }, (_, i) => i + 1)

  const handleTimeChange = (
    date: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange: (...event: any[]) => void,
    type: 'hour' | 'minute' | 'ampm',
    value: string
  ) => {
    if (date) {
      const newDate = new Date(date)
      if (type === 'hour') {
        newDate.setHours((parseInt(value) % 12) + (newDate.getHours() >= 12 ? 12 : 0))
      } else if (type === 'minute') {
        newDate.setMinutes(parseInt(value))
      } else if (type === 'ampm') {
        const currentHours = newDate.getHours()
        newDate.setHours(value === 'PM' ? currentHours + 12 : currentHours - 12)
      }
      onChange(newDate.toISOString())
    }
  }

  return (
    <FormField
      control={control}
      name={formFieldName}
      render={({ field: formField }) => (
        <FormItem className={clsx('flex flex-1 flex-col', field.widget === 'hidden' && 'hidden')}>
          <FormLabel>{field.title}</FormLabel>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn('w-full pl-3 text-left font-normal', !formField.value && 'text-muted-foreground')}
                disabled={field.readonly}
              >
                {formField.value ? (
                  format(new Date(formField.value as string), 'PPpp')
                ) : (
                  <span>MM/DD/YYYY hh:mm aa</span>
                )}
                <IconCalendarClockOutline className="ml-auto size-5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <div className="sm:flex">
                <Calendar
                  mode="single"
                  selected={formField.value ? new Date(formField.value as string) : undefined}
                  onSelect={(selectedDate: Date | undefined) => {
                    if (selectedDate) {
                      formField.onChange(selectedDate.toISOString())
                    }
                  }}
                  initialFocus
                />
                <div className="flex flex-col divide-y sm:h-[300px] sm:flex-row sm:divide-x sm:divide-y-0">
                  <ScrollArea className="w-64 sm:w-auto">
                    <div className="flex p-2 sm:flex-col">
                      {hours.reverse().map((hour) => (
                        <Button
                          key={hour}
                          size="icon"
                          variant={
                            formField.value && new Date(formField.value as string).getHours() % 12 === hour % 12
                              ? 'default'
                              : 'ghost'
                          }
                          className="aspect-square shrink-0 sm:w-full"
                          onClick={() =>
                            handleTimeChange(formField.value as string, formField.onChange, 'hour', hour.toString())
                          }
                        >
                          {hour}
                        </Button>
                      ))}
                    </div>
                    <ScrollBar orientation="horizontal" className="sm:hidden" />
                  </ScrollArea>
                  <ScrollArea className="w-64 sm:w-auto">
                    <div className="flex p-2 sm:flex-col">
                      {Array.from({ length: 12 }, (_, i) => i * 5).map((minute) => (
                        <Button
                          key={minute}
                          size="icon"
                          variant={
                            formField.value && new Date(formField.value as string).getMinutes() === minute
                              ? 'default'
                              : 'ghost'
                          }
                          className="aspect-square shrink-0 sm:w-full"
                          onClick={() =>
                            handleTimeChange(formField.value as string, formField.onChange, 'minute', minute.toString())
                          }
                        >
                          {minute}
                        </Button>
                      ))}
                    </div>
                    <ScrollBar orientation="horizontal" className="sm:hidden" />
                  </ScrollArea>
                  <ScrollArea className="">
                    <div className="flex p-2 sm:flex-col">
                      {['AM', 'PM'].map((ampm) => (
                        <Button
                          key={ampm}
                          size="icon"
                          variant={
                            formField.value &&
                            ((ampm === 'AM' && new Date(formField.value as string).getHours() < 12) ||
                              (ampm === 'PM' && new Date(formField.value as string).getHours() >= 12))
                              ? 'default'
                              : 'ghost'
                          }
                          className="aspect-square shrink-0 sm:w-full"
                          onClick={() => handleTimeChange(formField.value as string, formField.onChange, 'ampm', ampm)}
                        >
                          {ampm}
                        </Button>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              </div>
            </PopoverContent>
          </Popover>
          <FormDescription>{field.description}</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}
